import dayjs, { Dayjs } from 'dayjs';
import { UIEvent, useMemo } from 'react';
import { unique } from 'remeda';
import { useScrollPaginateHandler } from 'src/utils/hooks/use-scroll-paginate-handler';
import { sortByDate } from 'src/utils/sorting';
import { messageRegistry } from './index';
import { ChildStatus, MESSAGE_TYPE_IDS, MessageTypeId, TimelineEntry } from './types';

export { useReadinessWithTimeout } from './use-readiness-with-timeout';

export function useMergedTimeline(): TimelineEntry<unknown>[] {
  const itemsByType = MESSAGE_TYPE_IDS.map((id) => {
    const reg = messageRegistry[id];

    return reg ? reg.useItems() : ([] as TimelineEntry<unknown>[]);
  });

  return useMemo(
    () => itemsByType.flat().toSorted(sortByDate('timestamp', true)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps are the per-type arrays in stable order
    itemsByType,
  );
}

export function useAllBlockingResolved(): { shouldRender: boolean } {
  const readinessByType = MESSAGE_TYPE_IDS.map((id) => {
    const reg = messageRegistry[id];

    const status = reg ? reg.useReadiness() : ChildStatus.Ready;
    return { reg, status };
  });

  // Error and Timeout are intentionally treated as "resolved enough" so that a
  // failed child does not permanently block sibling registrations from rendering.
  const shouldRender = readinessByType.every(({ reg, status }) => {
    if (!reg?.blockingForInitialRender) return true;
    return (
      status === ChildStatus.Ready ||
      status === ChildStatus.Refetching ||
      status === ChildStatus.Error ||
      status === ChildStatus.Timeout
    );
  });

  return { shouldRender };
}

export interface ChatPagination {
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => Promise<void>;
}

export function useChatPagination(): ChatPagination {
  const paginationStates = MESSAGE_TYPE_IDS.map((id) => {
    const reg = messageRegistry[id];
    return reg?.usePaginationState ? reg.usePaginationState() : undefined;
  });

  const loadMore = useChatLoadMore();

  const hasMore = paginationStates.some((s) => Boolean(s?.hasMore));
  const isLoadingMore = paginationStates.some((s) => Boolean(s?.isLoadingMore));

  return useMemo(
    () => ({ hasMore, isLoadingMore, loadMore }),
    [hasMore, isLoadingMore, loadMore],
  );
}

export function useChatScrollHandler(): (e: UIEvent) => Promise<void> {
  const { hasMore, loadMore } = useChatPagination();

  return useScrollPaginateHandler({
    loadMore,
    enabled: hasMore,
    thresholdPx: 200,
    reversed: true,
  });
}

export function useChatLoadMore(): () => Promise<void> {
  const loadMoreFnsByType = MESSAGE_TYPE_IDS.map((id) => {
    const reg = messageRegistry[id];

    return reg?.useLoadMore ? reg.useLoadMore() : undefined;
  });

  return useMemo(
    () => async () => {
      await Promise.all(loadMoreFnsByType.filter(Boolean).map((fn) => fn!()));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps are the per-type fn refs in stable order
    loadMoreFnsByType,
  );
}

export function useTimelineBoundaryTimestamps(): Dayjs[] {
  const siblingItemArrays = MESSAGE_TYPE_IDS.filter(
    (id) => id !== MessageTypeId.ChatMessage,
  ).map((id) => {
    const reg = messageRegistry[id];
    return reg ? reg.useItems() : [];
  });

  return useMemo(
    () =>
      unique(siblingItemArrays.flat().map((entry) => entry.timestamp))
        .toSorted((a, b) => new Date(a).getTime() - new Date(b).getTime())
        .map((ts) => dayjs(ts)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps are the per-type arrays in stable order
    siblingItemArrays,
  );
}
